/**
 * Travelix Firestore data layer — the only place in the app that touches Firestore.
 *
 * Every function returns { data, error } so callers can handle both branches
 * without try/catch noise. This mirrors the pattern proven in the RN app.
 *
 * The `db` / `auth` handles come from the platform-agnostic factory in ./firebase,
 * so this file works identically on web (Vite), Capacitor Android/iOS, and any
 * future host without changes.
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  deleteField,
  getDocs,
  collection,
  query,
  orderBy,
  writeBatch,
  runTransaction,
  arrayUnion,
  arrayRemove,
  Timestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getFirebaseFunctions, getFirestoreDb } from "./firebase";
import { toResult, type DbResult } from "./result";
import type {
  User,
  Trip,
  Activity,
  ActivityCreate,
  ActivityUpdate,
  ExpenseEntry,
  TripBudget,
  TripSummary,
  InviteEntry,
  TravelerRole,
  TripMemberEntry,
  TripIdea,
  TripPoll,
  PackingItem,
  TripDoc,
} from "./types";

export type { DbResult } from "./result";

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

function chunkArray<T>(arr: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function tripRef(tripId: string) {
  return doc(getFirestoreDb(), "trips", tripId);
}

/** Patch fields on trips/{tripId} and bump updatedAt — the shape most trip writes share. */
function patchTrip(tripId: string, patch: Record<string, unknown>): Promise<DbResult<void>> {
  return toResult(() => updateDoc(tripRef(tripId), { ...patch, updatedAt: Timestamp.now() }));
}

/** The trip fields stored as embedded arrays of `{ id, … }` entries. */
type TripArrayField = "expenses" | "ideas" | "polls" | "packingItems";
type TripArray<K extends TripArrayField> = NonNullable<TripDoc[K]>;

/** Append one entry to an embedded trip array (atomic arrayUnion). */
function appendToTripArray<K extends TripArrayField>(
  tripId: string,
  field: K,
  entry: TripArray<K>[number],
): Promise<DbResult<void>> {
  return patchTrip(tripId, { [field]: arrayUnion(entry) });
}

/**
 * Read → transform one embedded array → write it back. Not atomic with
 * concurrent writers (last write wins); acceptable while these arrays are
 * small and edits infrequent. `transform` may throw to abort (e.g. "not found").
 */
function replaceTripArray<K extends TripArrayField>(
  tripId: string,
  field: K,
  transform: (current: TripArray<K>) => TripArray<K>,
): Promise<DbResult<void>> {
  return toResult(async () => {
    const ref = tripRef(tripId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Trip not found");
    const current = (snap.data()[field] ?? []) as TripArray<K>;
    await updateDoc(ref, { [field]: transform(current), updatedAt: Timestamp.now() });
  });
}

// ---------------------------------------------------------------------------
// User functions
// ---------------------------------------------------------------------------

export async function getUserByEmail(email: string): Promise<DbResult<User>> {
  return toResult(async () => {
    const db = getFirestoreDb();
    const ref = doc(db, "users", email.toLowerCase());
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      throw new Error("User not found");
    }
    return { id: snap.id, ...snap.data() } as User;
  });
}

/**
 * Backfill a stale/missing miniature on one entry in users/{email}.trips[].
 *
 * Why this exists: per D-015 v2 has no photo write path, so any time a
 * trip's coverImage is updated out-of-band (Firestore console, v1 leftover,
 * migration script), the embedded TripSummary.miniature is left stale.
 * The dashboard cover fallback (useTripCoverFallback) fetches the fresh
 * coverImage; this write persists it so the next session is a zero-read
 * dashboard again.
 *
 * Runs inside a transaction: two cards repairing the same user doc in
 * parallel each retry cleanly instead of overwriting each other. No-ops
 * when the miniature is already correct or the trip isn't in trips[].
 */
export async function repairTripSummaryMiniature(
  email: string,
  tripId: string,
  miniature: string,
): Promise<DbResult<void>> {
  return toResult(async () => {
    const db = getFirestoreDb();
    const ref = doc(db, "users", email.toLowerCase());
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) return;
      const trips: TripSummary[] = snap.data().trips ?? [];
      const idx = trips.findIndex((t) => t.id === tripId);
      if (idx < 0) return;
      if (trips[idx]!.miniature === miniature) return;
      const next = trips.map((t, i) => (i === idx ? { ...t, miniature } : t));
      tx.update(ref, { trips: next });
    });
  });
}

export async function createUserDoc(
  email: string,
  uid: string,
  name: string,
): Promise<DbResult<void>> {
  return toResult(async () => {
    const db = getFirestoreDb();
    const ref = doc(db, "users", email.toLowerCase());
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      // No existing doc — create fresh
      await setDoc(ref, {
        uid,
        name,
        email: email.toLowerCase(),
        trips: [],
        pendingInvites: [],
        createdAt: Timestamp.now(),
      });
    } else if (!snap.data().uid) {
      // Stub exists (invited but not yet registered) — fill in uid/name/createdAt
      // merge:true preserves existing trips[] and pendingInvites[]
      await setDoc(
        ref,
        {
          uid,
          name,
          email: email.toLowerCase(),
          createdAt: Timestamp.now(),
        },
        { merge: true },
      );
    }
    // else: fully registered doc already exists — nothing to do

  });
}

// ---------------------------------------------------------------------------
// Trip functions
// ---------------------------------------------------------------------------

export async function getTripById(tripId: string): Promise<DbResult<Trip>> {
  return toResult(async () => {
    const db = getFirestoreDb();
    const ref = doc(db, "trips", tripId);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      throw new Error("Trip not found");
    }
    return { id: snap.id, ...snap.data() } as Trip;
  });
}

export async function createTripWithUser(params: {
  name: string;
  destinations: string[];
  startDate: Timestamp;
  endDate: Timestamp;
  coverImage: string | null;
  ownerUid: string;
  ownerName: string;
  ownerEmail: string;
  invitedEmails: string[];
}): Promise<DbResult<CreateTripResult>> {
  return toResult(async () => {
    const db = getFirestoreDb();
    const {
      name,
      destinations,
      startDate,
      endDate,
      coverImage,
      ownerUid,
      ownerName,
      ownerEmail,
      invitedEmails,
    } = params;

    const batch = writeBatch(db);

    const tripRef = doc(collection(db, "trips"));
    const durationDays = Math.ceil(
      (endDate.toMillis() - startDate.toMillis()) / 86400000,
    );

    const tripData = {
      name,
      destinations,
      startDate,
      endDate,
      durationDays,
      coverImage: coverImage ?? null,
      memberIds: [ownerUid],
      members: {
        [ownerUid]: {
          role: "owner" as TravelerRole,
          name: ownerName,
          email: ownerEmail.toLowerCase(),
          addedAt: Timestamp.now(),
        },
      },
      // Filled by the inviteToTrip callable below, so a failed invite
      // doesn't leave a pending email without a matching user-doc entry.
      pendingInviteEmails: [],
      expenses: [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    batch.set(tripRef, tripData);

    const tripSummary: TripSummary = {
      id: tripRef.id,
      name,
      city: destinations[0] ?? "",
      startDate,
      endDate,
      miniature: coverImage ?? null,
      role: "owner",
    };

    batch.update(doc(db, "users", ownerEmail.toLowerCase()), {
      trips: arrayUnion(tripSummary),
    });

    await batch.commit();

    // The trip exists now; an invite that fails doesn't undo it, but the
    // caller is told which emails didn't go out.
    const results = await Promise.all(
      invitedEmails.map((email) => inviteUserToTrip(tripRef.id, email, "traveler")),
    );
    const failedInvites = invitedEmails.filter((_, i) => results[i]!.error);

    return { tripId: tripRef.id, failedInvites };
  });
}

/**
 * Fields that must stay in sync between the trip doc and every member's
 * users/{email}.trips[] summary. If you touch any of these, you MUST cascade.
 */
export interface CascadingTripUpdate {
  name?: string;
  /** First destination becomes TripSummary.city */
  destinations?: string[];
  startDate?: Timestamp;
  endDate?: Timestamp;
  /** Also updates durationDays derived from startDate/endDate below when either is set. */
  coverImage?: string | null;
}

/**
 * Update fields on the trip doc. A Firestore trigger (see
 * apps/backend/functions/src/routes/trip-cascade.route.ts) mirrors the change
 * into every member's users/{email}.trips[] with Admin credentials — a
 * client-side cascade is impossible because the users/{email} rule only
 * permits self-writes.
 *
 * Consequence: the dashboard for OTHER members updates on a short trigger-run
 * delay, not synchronously. The caller's own trip screen (which reads
 * trips/{tripId} directly) shows the change immediately.
 */
export async function updateTripSummaryFields(
  tripId: string,
  updates: CascadingTripUpdate,
): Promise<DbResult<void>> {
  const tripPatch: Record<string, unknown> = { ...updates };
  if (updates.startDate && updates.endDate) {
    tripPatch.durationDays = Math.ceil(
      (updates.endDate.toMillis() - updates.startDate.toMillis()) / 86400000,
    );
  }
  return patchTrip(tripId, tripPatch);
}

/**
 * Backward-compat wrapper. Prefer updateTripSummaryFields for new code.
 */
export async function updateTripName(
  tripId: string,
  newName: string,
): Promise<DbResult<void>> {
  return updateTripSummaryFields(tripId, { name: newName });
}

/**
 * Non-cascading trip update — currency lives only on the trip doc.
 */
export async function updateTripCurrency(
  tripId: string,
  currency: string,
): Promise<DbResult<void>> {
  return patchTrip(tripId, { currency });
}

/**
 * Non-cascading trip update — pin (or clear) a Google Maps URL to one day
 * of the itinerary. `dayKey` is the local yyyy-MM-dd used by the timeline.
 * Passing `null` for `mapsUrl` removes the field via `deleteField()`.
 */
export async function setTripDayMapsUrl(
  tripId: string,
  dayKey: string,
  mapsUrl: string | null,
): Promise<DbResult<void>> {
  return patchTrip(tripId, { [`days.${dayKey}.mapsUrl`]: mapsUrl ?? deleteField() });
}

/**
 * Non-cascading trip update — budget lives only on the trip doc. Passing
 * `null` clears the budget (Firestore rejects `undefined` on updates,
 * so we use `deleteField()` via a null explicit sentinel).
 */
export async function updateTripBudget(
  tripId: string,
  budget: TripBudget | null,
): Promise<DbResult<void>> {
  // Firestore treats `null` as an explicit value on updates, which is what
  // we want here (clear the field). `deleteField()` would also work but null
  // is simpler and reads back as absent to consumers using `?.` chains.
  return patchTrip(tripId, { budget });
}

// ---------------------------------------------------------------------------
// Member management
// ---------------------------------------------------------------------------

/**
 * Remove a member from a trip and from their own user doc. Owner-only action
 * for kicking someone; also the primitive that `leaveTrip` uses.
 *
 * Cleans:
 *   trips/{tripId}.memberIds[]         — arrayRemove(memberUid)
 *   trips/{tripId}.members[memberUid]  — delete
 *   users/{email}.trips[]              — filter out this trip
 */
export async function removeMemberFromTrip(
  tripId: string,
  memberUid: string,
  _memberEmail: string,
): Promise<DbResult<void>> {
  // The trip-cascade trigger observes memberIds shrinking and prunes the
  // trip summary from the removed member's users/{email}.trips[]. Client
  // can't do that write itself — users/{email} rules are self-only.
  return patchTrip(tripId, {
    memberIds: arrayRemove(memberUid),
    [`members.${memberUid}`]: deleteField(),
  });
}

/**
 * Leave a trip you're a member of. Blocked for owners — an owner must delete
 * or transfer ownership. Calls removeMemberFromTrip under the hood.
 */
export async function leaveTrip(
  trip: Trip,
  userUid: string,
  userEmail: string,
): Promise<DbResult<void>> {
  const entry = trip.members[userUid];
  if (!entry) return { data: null, error: new Error("Not a member of this trip") };
  if (entry.role === "owner") {
    return {
      data: null,
      error: new Error("Owners can't leave — delete the trip or transfer ownership first."),
    };
  }
  return removeMemberFromTrip(trip.id, userUid, userEmail);
}

/**
 * Delete a trip completely — owner-only.
 *
 *   1. Delete every activity in the trips/{tripId}/activities subcollection.
 *      Firestore batches cap at 500 ops, so we chunk if the subcollection is huge.
 *   2. Delete the trip doc itself.
 *
 * The trip-cascade trigger observes the trip deletion and drops the trip
 * summary from every prior member's users/{email}.trips[] — that write is
 * server-side because users/{email} rules are self-only.
 *
 * The associated Storage cover is best-effort deleted separately by the caller,
 * so this function stays Firestore-only (Storage errors shouldn't fail the delete).
 */
export async function deleteTrip(
  tripId: string,
  _memberEmails: string[],
): Promise<DbResult<void>> {
  return toResult(async () => {
    const db = getFirestoreDb();

    const activitiesQuery = query(collection(db, "trips", tripId, "activities"));
    const activitiesSnap = await getDocs(activitiesQuery);
    for (const chunk of chunkArray(activitiesSnap.docs, 400)) {
      const batch = writeBatch(db);
      for (const d of chunk) batch.delete(d.ref);
      await batch.commit();
    }

    await deleteDoc(doc(db, "trips", tripId));
  });
}

// ---------------------------------------------------------------------------
// Activity functions
// ---------------------------------------------------------------------------

export async function getTripActivities(
  tripId: string,
): Promise<DbResult<Activity[]>> {
  return toResult(async () => {
    const db = getFirestoreDb();
    const q = query(
      collection(db, "trips", tripId, "activities"),
      orderBy("startTime"),
    );
    const snap = await getDocs(q);
    const activities: Activity[] = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as Activity[];
    return activities;
  });
}

export async function createActivity(
  tripId: string,
  data: ActivityCreate,
): Promise<DbResult<Activity>> {
  return toResult(async () => {
    const db = getFirestoreDb();
    const ref = await addDoc(
      collection(db, "trips", tripId, "activities"),
      data,
    );
    return { id: ref.id, ...data } as Activity;
  });
}

export async function updateActivity(
  tripId: string,
  activityId: string,
  data: ActivityUpdate,
): Promise<DbResult<void>> {
  return toResult(async () => {
    const db = getFirestoreDb();
    await updateDoc(doc(db, "trips", tripId, "activities", activityId), data);
  });
}

export async function deleteActivity(
  tripId: string,
  activityId: string,
): Promise<DbResult<void>> {
  return toResult(async () => {
    const db = getFirestoreDb();
    await deleteDoc(doc(db, "trips", tripId, "activities", activityId));
  });
}

// ---------------------------------------------------------------------------
// Expense functions
// ---------------------------------------------------------------------------

export async function addExpenseToTrip(
  tripId: string,
  expense: ExpenseEntry,
): Promise<DbResult<void>> {
  return appendToTripArray(tripId, "expenses", expense);
}

export async function removeExpenseFromTrip(
  tripId: string,
  expenseId: string,
): Promise<DbResult<void>> {
  // arrayRemove (atomic) needs the exact stored entry, so read it first.
  return toResult(async () => {
    const snap = await getDoc(tripRef(tripId));
    if (!snap.exists()) throw new Error("Trip not found");
    const expenses: ExpenseEntry[] = snap.data().expenses ?? [];
    const expense = expenses.find((e) => e.id === expenseId);
    if (!expense) throw new Error("Expense not found");
    await updateDoc(tripRef(tripId), {
      expenses: arrayRemove(expense),
      updatedAt: Timestamp.now(),
    });
  });
}

/**
 * Replace one expense in-place. Reads the trip doc, patches the matching
 * ExpenseEntry by id, writes the full array back. Not atomic with concurrent
 * writers — the last write wins. Acceptable while trip.expenses is embedded.
 */
export async function updateExpenseInTrip(
  tripId: string,
  updated: ExpenseEntry,
): Promise<DbResult<void>> {
  return replaceTripArray(tripId, "expenses", (expenses) => {
    if (!expenses.some((e) => e.id === updated.id)) throw new Error("Expense not found");
    return expenses.map((e) =>
      e.id === updated.id ? { ...updated, updatedAt: Timestamp.now() } : e,
    );
  });
}

// ---------------------------------------------------------------------------
// Trip Ideas (wishlist)
// ---------------------------------------------------------------------------

export async function addTripIdea(
  tripId: string,
  idea: TripIdea,
): Promise<DbResult<void>> {
  return appendToTripArray(tripId, "ideas", idea);
}

export async function removeTripIdea(
  tripId: string,
  ideaId: string,
): Promise<DbResult<void>> {
  return replaceTripArray(tripId, "ideas", (ideas) =>
    ideas.filter((i) => i.id !== ideaId),
  );
}

/**
 * Mark an idea as scheduled by pinning it to the created activity. Called by
 * the timeline flow after createActivity succeeds — the caller passes the new
 * activity's id.
 */
export async function markIdeaScheduled(
  tripId: string,
  ideaId: string,
  activityId: string,
): Promise<DbResult<void>> {
  return replaceTripArray(tripId, "ideas", (ideas) =>
    ideas.map((i) =>
      i.id === ideaId ? { ...i, scheduledActivityId: activityId } : i,
    ),
  );
}

// ---------------------------------------------------------------------------
// Trip Polls
// ---------------------------------------------------------------------------

export async function addTripPoll(
  tripId: string,
  poll: TripPoll,
): Promise<DbResult<void>> {
  return appendToTripArray(tripId, "polls", poll);
}

export async function castPollVote(
  tripId: string,
  pollId: string,
  userId: string,
  optionId: string,
): Promise<DbResult<void>> {
  return replaceTripArray(tripId, "polls", (polls) =>
    polls.map((p) =>
      p.id === pollId && !p.closed
        ? { ...p, votes: { ...p.votes, [userId]: optionId } }
        : p,
    ),
  );
}

export async function closeTripPoll(
  tripId: string,
  pollId: string,
): Promise<DbResult<void>> {
  return replaceTripArray(tripId, "polls", (polls) =>
    polls.map((p) => (p.id === pollId ? { ...p, closed: true } : p)),
  );
}

export async function removeTripPoll(
  tripId: string,
  pollId: string,
): Promise<DbResult<void>> {
  return replaceTripArray(tripId, "polls", (polls) =>
    polls.filter((p) => p.id !== pollId),
  );
}

// ---------------------------------------------------------------------------
// Packing / Bring List
// ---------------------------------------------------------------------------

export async function addPackingItem(
  tripId: string,
  item: PackingItem,
): Promise<DbResult<void>> {
  return appendToTripArray(tripId, "packingItems", item);
}

export async function claimPackingItem(
  tripId: string,
  itemId: string,
  userId: string,
  userName: string,
): Promise<DbResult<void>> {
  return replaceTripArray(tripId, "packingItems", (items) =>
    items.map((i) =>
      i.id === itemId
        ? {
            ...i,
            claimedBy: userId,
            claimedByName: userName,
            claimedAt: Timestamp.now(),
          }
        : i,
    ),
  );
}

export async function unclaimPackingItem(
  tripId: string,
  itemId: string,
): Promise<DbResult<void>> {
  return replaceTripArray(tripId, "packingItems", (items) =>
    items.map((i) => {
      if (i.id !== itemId) return i;
      // Strip claim fields entirely so the shape matches unclaimed items
      const { claimedBy: _c, claimedByName: _n, claimedAt: _a, ...rest } = i;
      void _c; void _n; void _a;
      return rest;
    }),
  );
}

export async function removePackingItem(
  tripId: string,
  itemId: string,
): Promise<DbResult<void>> {
  return replaceTripArray(tripId, "packingItems", (items) =>
    items.filter((i) => i.id !== itemId),
  );
}

// ---------------------------------------------------------------------------
// Invite functions
// ---------------------------------------------------------------------------

export interface CreateTripResult {
  tripId: string;
  /** Emails whose invite couldn't be sent; the trip itself was created. */
  failedInvites: string[];
}

export type InviteStatus = "invited" | "already-member" | "already-pending";

/**
 * Invite an email to a trip as the signed-in member, via the `inviteToTrip`
 * callable. It runs server-side because the invitee's users/{email} doc is
 * theirs: rules don't let a client update it, so a client-side write failed
 * for anyone who already had a doc. The function reads the trip's name,
 * city, cover and the inviter's name itself.
 */
export async function inviteUserToTrip(
  tripId: string,
  inviteeEmail: string,
  role: TravelerRole,
): Promise<DbResult<InviteStatus>> {
  return toResult(async () => {
    const fn = httpsCallable<
      { tripId: string; email: string; role: TravelerRole },
      { status: InviteStatus }
    >(getFirebaseFunctions(), "inviteToTrip");
    const result = await fn({ tripId, email: inviteeEmail.trim().toLowerCase(), role });
    return result.data.status;
  });
}

/**
 * Accept a trip invite. Batch write across the invitee's user doc + the
 * trip doc:
 *   • Remove entry from users/{email}.pendingInvites[]
 *   • Add TripSummary to users/{email}.trips[]
 *   • Remove email from trips/{tripId}.pendingInviteEmails[]
 *   • Add uid to trips/{tripId}.memberIds[]
 *   • Set trips/{tripId}.members[uid] = { role, name, email, addedAt }
 *
 * Requires the invitee to already be authenticated (uid + email known) and
 * their email to appear in the trip's pendingInviteEmails[] (enforced by
 * Firestore rules for security).
 */
export async function acceptTripInvite(
  invite: InviteEntry,
  user: { uid: string; email: string; name: string },
): Promise<DbResult<void>> {
  return toResult(async () => {
    const db = getFirestoreDb();
    const batch = writeBatch(db);

    // Refetch the trip so we get the current name / dates / dests / cover for
    // the TripSummary — the invite entry only carries a snapshot from send-time.
    const tripRef = doc(db, "trips", invite.tripId);
    const tripSnap = await getDoc(tripRef);
    if (!tripSnap.exists()) {
      throw new Error("Trip no longer exists");
    }
    const tripData = tripSnap.data();
    const summary: TripSummary = {
      id: invite.tripId,
      name: tripData.name,
      city: (tripData.destinations?.[0] as string | undefined) ?? invite.city,
      startDate: tripData.startDate,
      endDate: tripData.endDate,
      miniature: (tripData.coverImage as string | null | undefined) ?? invite.miniature ?? null,
      role: invite.role,
    };

    // User doc side
    const userRef = doc(db, "users", user.email.toLowerCase());
    batch.update(userRef, {
      pendingInvites: arrayRemove(invite),
      trips: arrayUnion(summary),
    });

    // Trip doc side — add to memberIds and members map, remove from pending
    batch.update(tripRef, {
      pendingInviteEmails: arrayRemove(user.email.toLowerCase()),
      memberIds: arrayUnion(user.uid),
      [`members.${user.uid}`]: {
        role: invite.role,
        name: user.name,
        email: user.email.toLowerCase(),
        addedAt: Timestamp.now(),
      } satisfies TripMemberEntry,
      updatedAt: Timestamp.now(),
    });

    await batch.commit();
  });
}

/**
 * Decline a trip invite. Removes the pending-invite entry from both sides,
 * with no member-side effect.
 *
 * The trip doc write only touches `pendingInviteEmails` — the invitee doesn't
 * need trip read access, and Firestore rules should permit any signed-in user
 * whose email is in the array to remove themselves.
 */
export async function denyTripInvite(
  invite: InviteEntry,
  userEmail: string,
): Promise<DbResult<void>> {
  return toResult(async () => {
    const db = getFirestoreDb();
    const batch = writeBatch(db);

    const email = userEmail.toLowerCase();
    batch.update(doc(db, "users", email), {
      pendingInvites: arrayRemove(invite),
    });
    batch.update(doc(db, "trips", invite.tripId), {
      pendingInviteEmails: arrayRemove(email),
    });

    await batch.commit();
  });
}

// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------

export interface DeleteAccountResult {
  /** Trips where the user was the only member. */
  deletedTrips: string[];
  /** Shared trips the user owned; ownership moved to another member. */
  transferredTrips: string[];
  /** Shared trips the user was a non-owner member of. */
  leftTrips: string[];
}

/**
 * Permanently delete the signed-in user's account and data, via the
 * `deleteAccount` callable. Server-side because it rewrites other members'
 * trips and user docs; see apps/backend/functions/src/helpers/
 * account-deletion.helper.ts for exactly what is deleted and what is kept.
 *
 * The Auth user is deleted server-side too, so the caller should sign out
 * locally afterwards. Safe to retry after an error.
 */
export async function deleteMyAccount(): Promise<DbResult<DeleteAccountResult>> {
  return toResult(async () => {
    const fn = httpsCallable<void, DeleteAccountResult>(getFirebaseFunctions(), "deleteAccount");
    const result = await fn();
    return result.data;
  });
}
